import React, { useState } from 'react'
import DragAndDrop from '../../components/DragAndDropUpload'
import CsvFieldMapping from '../../components/CsvFieldMapping'
import GenericModal from '../GenericModal'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '../../utils/api'
import type { CsvHeader } from '../../models/CsvHeader'
import type { TaskResult } from '../../models/Task'
import { notification } from '../Notifications'

interface CsvMappingUploadProps {
  isOpen: boolean
  onClose: () => void
  modalTitle: string
  csvHeaderEndpoint: string
  uploadCsvEndpoint: string
  onSuccess?: () => void
}

const CsvMappingUpload: React.FC<CsvMappingUploadProps> = ({ isOpen, modalTitle, onClose, csvHeaderEndpoint, uploadCsvEndpoint, onSuccess = () => { } }) => {
  const { data: backendHeaders, isPending: csvHeaderLoading } = useQuery<CsvHeader[]>({
    queryKey: ['csvheaderapi-' + csvHeaderEndpoint],
    staleTime: Infinity,
    queryFn: async () => {
      const result = await api.get(csvHeaderEndpoint)
      return result.data
    }
  })

  const [file, setFile] = useState<File | null>(null)
  const [taskId, setTaskId] = useState<string>('')
  const [userHeaders, setUserHeaders] = useState<string[]>([])
  const [previewData, setPreviewData] = useState<string[][]>([])

  const closeUploadModal = (): void => {
    setTaskId('')
    setFile(null)
    onClose()
  }

  const handleFile = (file: File): void => {
    setFile(file)
    const reader = new FileReader()
    reader.readAsText(file)

    reader.onload = (e) => {
      const csvString = (reader.result ?? '') as string
      const lines = csvString.split('\n')
      const headerLine = lines[0]
      const headers = headerLine.split(',').map(l => l.trim())
      setUserHeaders(headers)

      // Parse the first 4 rows of data keeping in mind that strings may have commas in them
      const previewData = lines.slice(1, 5).map(line => {
        const cells = []
        let currentCell = ''
        let insideQuotes = false

        for (let i = 0; i < line.length; i++) {
          const char = line[i]

          if (char === '"' && (i === 0 || line[i - 1] !== '\\')) {
            insideQuotes = !insideQuotes
          }

          if (char === ',' && !insideQuotes) {
            cells.push(currentCell.trim())
            currentCell = ''
          } else {
            currentCell += char
          }
        }

        cells.push(currentCell.trim())
        return cells
      })
      setPreviewData(previewData)
    }
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (mapping: Record<string, string>): Promise<TaskResult> => {
      const result = await api.post(uploadCsvEndpoint, file, {
        url: uploadCsvEndpoint,
        headers: {
          'Content-Type': 'text/csv',
          'Classranked-CSV-Mapping': JSON.stringify(mapping)
        }
      })
      return result.data
    }
  })

  const handleUpload = async (mapping: Record<string, string>): Promise<void> => {
    if (file) {
      try {
        const res = await mutateAsync(mapping)
        setTaskId(res.taskId)
        setFile(null)
        onSuccess()
      } catch (error) {
        console.error('Error uploading file', error)
        notification('Error uploading CSV', { variant: 'error', subTitle: 'File Upload Error' })
      }
    }
  }

  const parsedModalTitle = taskId ? 'Upload Complete' : `Upload ${modalTitle}`

  function getAlertType (): string | null {
    if (taskId) {
      return 'success'
    }
    return null
  }

  return (
    <GenericModal action={false} maxWidth='sm' title={parsedModalTitle} showCloseIcon={false} isOpen={isOpen} onClose={onClose}>
      <>
        {file === null && <DragAndDrop loading={csvHeaderLoading} taskId={taskId} onDrop={handleFile} onClose={closeUploadModal} alertType={getAlertType()} validationConfig={null} />
        }
        {file !== null && <CsvFieldMapping
          title={modalTitle}
          onClose={() => { setFile(null) }}
          loading={isPending ?? !backendHeaders}
          userHeaders={userHeaders}
          backendHeaders={backendHeaders ?? []}
          previewData={previewData ?? []}
          onSuccess={(mapping) => {
            void handleUpload(mapping)
          }}
        />
        }
      </>
    </GenericModal>
  )
}

export default CsvMappingUpload
